import { useState } from "react";
import { log } from "../../../../commonfunctions/logger";
import { isAddressValid } from "../../../../components/Address/util";
import { Address, AlertContent } from "../../../../interfaces/interfaces";
import { AlertType } from "../../../../interfaces/enums";
import { subdomainTypes } from "../../../../context/availableSubdomains";
import { Form } from "../AUAddressForm";
import { addressService } from "../services/addressService";
import { isAddressFormValid } from "./addressFormValidation";

interface UseAddressFormProps {
  branch: subdomainTypes | "";
  onSuccess: () => void;
  setToast: (toast: AlertContent | null) => void;
}

const initialFormState: Form = {
  address_city: "",
  address_postcode: "",
  state: "",
  ship_to_name: "",
  address_line1: "",
  address_line2: "",
  address_line3: "",
  address_line4: "",
  address_country: "Australia",
  is_primary_address: false,
};

export const useAddressForm = ({ branch, onSuccess, setToast }: UseAddressFormProps) => {
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [form, setForm] = useState<Form>(initialFormState);
  const [addressModified, setAddressModified] = useState<boolean>(false);

  const initializeEdit = (address: Address) => {
    setSelectedAddress(address);
    setForm({ ...initialFormState, ...address });
    setAddressModified(true);
  };

  const resetForm = () => {
    setSelectedAddress(null);
    setForm(initialFormState);
    setAddressModified(false);
  };

  const isFormValid = (): boolean => {
    return isAddressFormValid({ form, branch, addressSelected: addressModified });
  };

  const handleFormChange = (newForm: Form) => {
    setForm(newForm);
    setAddressModified(true);
  };

  const handleAddressChange = (value: React.SetStateAction<Address | null>) => {
    if (typeof value === "function") {
      setSelectedAddress((prev) => {
        const newAddress = value(prev);
        setAddressModified(true);
        return newAddress;
      });
    } else {
      setSelectedAddress(value);
      setAddressModified(true);
    }
  };

  const updateAddress = async () => {
    if (!selectedAddress) {
      setToast({
        message: "Please select an address to update.",
        type: AlertType.Error,
      });
      return;
    }

    const addressToSave = branch === "au" ? form : selectedAddress;

    if (!isAddressValid(addressToSave, branch)) {
      setToast({
        message: "Please fill in all required fields.",
        type: AlertType.Error,
      });
      return;
    }

    try {
      await addressService.update(addressToSave, branch);
      setToast({
        message: "Address updated successfully.",
        type: AlertType.Success,
      });
      resetForm();
      onSuccess();
    } catch (error) {
      log(error, "Failed to edit address in /preferences");
      setToast({
        message: "Failed to update address. Please try again.",
        type: AlertType.Error,
      });
    }
  };

  return {
    selectedAddress,
    form,
    isFormValid,
    initializeEdit,
    resetForm,
    handleFormChange,
    handleAddressChange,
    updateAddress,
  };
};
